import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGetAllLabelledStockQuery } from "../toolkitServices/stockApi";
import { useGetLatestRatesQuery } from "../toolkitServices/rateApi";
import {
  FiCamera,
  FiStopCircle,
  FiRefreshCcw,
  FiSearch,
  FiX,
  FiChevronLeft,
  FiImage,
} from "react-icons/fi";

/* -------------------- tiny UI helpers -------------------- */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const Pill = ({ children, tone = "slate" }) => {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rose: "bg-rose-100 text-rose-700 border-rose-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${map[tone]}`}
    >
      {children}
    </span>
  );
};

const Btn = ({
  children,
  variant = "primary", // primary | secondary | danger
  className = "",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-[12px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[#9481ce] text-white hover:[#9481ce] focus:ring-[#9481ce]",
    secondary:
      "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-slate-300",
    danger: "bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-400",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

/* -------------------- parsing helpers -------------------- */
const extractItemCode = (raw = "") => {
  const text = String(raw || "").trim();
  if (!text) return "";

  if (text.startsWith("{") && text.endsWith("}")) {
    try {
      const obj = JSON.parse(text);
      return (
        obj?.itemCode ||
        obj?.ItemCode ||
        obj?.code ||
        obj?.Code ||
        obj?.item ||
        obj?.Item ||
        ""
      );
    } catch {}
  }

  try {
    if (text.startsWith("http://") || text.startsWith("https://")) {
      const u = new URL(text);
      const v = u.searchParams.get("itemCode") || u.searchParams.get("code");
      if (v) return v.trim();
    }
  } catch {}

  const m =
    text.match(/item\s*code\s*[:=-]\s*([A-Za-z0-9_-]+)/i) ||
    text.match(/\bcode\s*[:=-]\s*([A-Za-z0-9_-]+)/i);
  if (m?.[1]) return m[1].trim();

  return text;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtINR = (v) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(toNum(v));

/* -------------------- upgraded scan overlay -------------------- */
const ScanOverlay = ({ isOn, isLock }) => (
  <AnimatePresence>
    {isOn && (
      <motion.div
        className="pointer-events-none absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/55" />

        <motion.div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
          animate={{ opacity: [0.1, 0.22, 0.1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[86%] max-w-[540px] aspect-[4/3] sm:aspect-video">
            <motion.div
              className="absolute inset-0 rounded-3xl"
              style={{
                boxShadow:
                  "0 0 0 9999px rgba(0,0,0,0.25), 0 0 30px rgba(167,139,250,0.35)",
                border: "1px solid rgba(255,255,255,0.22)",
                background:
                  "radial-gradient(circle at 50% 30%, rgba(167,139,250,0.12), transparent 55%)",
              }}
              animate={{ scale: [1, 1.01, 1] }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {[
              "top-0 left-0 border-t border-l",
              "top-0 right-0 border-t border-r",
              "bottom-0 left-0 border-b border-l",
              "bottom-0 right-0 border-b border-r",
            ].map((pos, idx) => (
              <motion.div
                key={idx}
                className={`absolute ${pos} w-10 h-10 rounded-[18px]`}
                style={{
                  borderColor: "rgba(167,139,250,0.85)",
                  filter: "drop-shadow(0 0 10px rgba(167,139,250,0.35))",
                }}
                animate={{ opacity: [0.65, 1, 0.65] }}
                transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}

            <motion.div className="absolute inset-0 overflow-hidden rounded-3xl">
              <motion.div
                className="absolute left-0 right-0 h-[3px]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(34,211,238,0.9), rgba(167,139,250,0.95), rgba(34,211,238,0.9), transparent)",
                  boxShadow:
                    "0 0 18px rgba(34,211,238,0.55), 0 0 30px rgba(167,139,250,0.45)",
                }}
                initial={{ y: "8%" }}
                animate={{ y: ["8%", "88%", "8%"] }}
                transition={{
                  duration: 2.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>

            <AnimatePresence>
              {isLock && (
                <motion.div
                  className="absolute inset-0 rounded-3xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.14 }}
                  style={{
                    background:
                      "radial-gradient(circle at 50% 50%, rgba(16,185,129,0.22), transparent 55%)",
                  }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* -------------------- main component -------------------- */
const LabelledStockQrScan = () => {
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const lockRef = useRef(false);

  const fileInputRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [isCameraOn, setIsCameraOn] = useState(false);

  const [manualInput, setManualInput] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [searchCode, setSearchCode] = useState("");
  const [tab, setTab] = useState("scan"); // scan | result
  const [lockScan, setLockScan] = useState(false);

  const [isImageScanning, setIsImageScanning] = useState(false);

  const queryArgs = useMemo(
    () => ({
      page: 1,
      limit: 20,
      search: searchCode || "",
      status: activeOnly ? "active" : "",
    }),
    [searchCode, activeOnly]
  );

  const { data, isFetching, isSuccess } = useGetAllLabelledStockQuery(
    queryArgs,
    {
      skip: !searchCode,
    }
  );

  const rows = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data]
  );
  const top = rows?.[0];

  // ✅ latest rates fetch
  const { data: latestRatesData, isFetching: isRateFetching } =
    useGetLatestRatesQuery(undefined, {
      skip: !top?.categoryId || !top?.purityId,
    });

  const latestRates = useMemo(
    () => (Array.isArray(latestRatesData?.data) ? latestRatesData.data : []),
    [latestRatesData]
  );

  // ✅ match rate by categoryId + purityId
  const matchedRate = useMemo(() => {
    const catId = String(top?.categoryId || "");
    const purId = String(top?.purityId || "");
    if (!catId || !purId) return null;

    return (
      latestRates.find((r) => {
        const rCat = String(r?.categoryId?._id || r?.categoryId || "");
        const rPur = String(r?.purityId?._id || r?.purityId || "");
        return r?.isActive && rCat === catId && rPur === purId;
      }) || null
    );
  }, [latestRates, top?.categoryId, top?.purityId]);

  // ✅ calculation
  const calc = useMemo(() => {
    const netWt = toNum(top?.netWt);
    const ratePerGram = toNum(matchedRate?.ratePerGram);
    const metalAmount = netWt && ratePerGram ? netWt * ratePerGram : 0;

    const makingPerGram = toNum(top?.makingPerGram);
    const fixedMaking = toNum(top?.fixedMaking);
    const fixedAmount = toNum(top?.fixedAmount);
    const makingPercentage = toNum(top?.makingPercentage);
    const makingPercentAmount = metalAmount * (makingPercentage / 100);

    const makingAmount =
      (makingPerGram * netWt || 0) +
      fixedMaking +
      fixedAmount +
      makingPercentAmount;

    const stoneAmount = toNum(top?.totalStoneAmount);
    const diamondAmount = toNum(top?.totalDiamondAmount);
    const hallmarkCharge = toNum(top?.hallmarkCharge);

    const totalAmount =
      metalAmount + makingAmount + stoneAmount + diamondAmount + hallmarkCharge;

    return {
      netWt,
      ratePerGram,
      metalAmount,
      makingAmount,
      stoneAmount,
      diamondAmount,
      hallmarkCharge,
      totalAmount,
    };
  }, [top, matchedRate]);

  // ✅ images
  const imageUrls = useMemo(() => {
    const imgs = Array.isArray(top?.images) ? top.images : [];
    return imgs
      .map((im) => {
        if (!im) return "";
        if (typeof im === "string") return im;
        return (
          im?.url || im?.src || im?.path || im?.imageUrl || im?.publicUrl || ""
        );
      })
      .filter(Boolean);
  }, [top?.images]);

  const stopCamera = useCallback(() => {
    try {
      controlsRef.current?.stop?.();
    } catch {}
    try {
      readerRef.current?.reset?.();
    } catch {}
    setIsCameraOn(false);
  }, []);

  useEffect(() => {
    if (tab !== "scan") stopCamera();
  }, [tab, stopCamera]);

  const doSearch = useCallback((rawText) => {
    const code = extractItemCode(rawText);
    if (!code) {
      toast.error("Item code nahi mila. Valid item code enter/paste karo.");
      return;
    }
    setSearchCode(code);
    setManualInput(code);
    setTab("result");
  }, []);

  // ✅ UPDATED: scan QR from local image (with padding fallback)
  // ✅ UPDATED: scan QR from local image (robust: padding + scale + crop + threshold)
  const scanFromImageFile = useCallback(
    async (file) => {
      if (!file) return;

      // camera band kar do (and reset)
      if (isCameraOn) stopCamera();

      setIsImageScanning(true);
      const originalUrl = URL.createObjectURL(file);

      const loadImage = (url) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });

      const canvasDataUrl = ({
        img,
        crop = null, // {x,y,w,h}
        pad = 0,
        scale = 1,
        threshold = null, // number (0-255) OR null
      }) => {
        const sx = crop?.x ?? 0;
        const sy = crop?.y ?? 0;
        const sw = crop?.w ?? img.width;
        const sh = crop?.h ?? img.height;

        const cw = Math.round((sw + pad * 2) * scale);
        const ch = Math.round((sh + pad * 2) * scale);

        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");

        // white bg for quiet-zone
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, cw, ch);

        // draw image area
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(
          img,
          sx,
          sy,
          sw,
          sh,
          Math.round(pad * scale),
          Math.round(pad * scale),
          Math.round(sw * scale),
          Math.round(sh * scale)
        );

        // optional threshold (binarize) for low-contrast / glare
        if (typeof threshold === "number") {
          const id = ctx.getImageData(0, 0, cw, ch);
          const d = id.data;
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i],
              g = d[i + 1],
              b = d[i + 2];
            const v = 0.299 * r + 0.587 * g + 0.114 * b;
            const out = v > threshold ? 255 : 0;
            d[i] = d[i + 1] = d[i + 2] = out;
            d[i + 3] = 255;
          }
          ctx.putImageData(id, 0, 0);
        }

        return canvas.toDataURL("image/png");
      };

      const tryDecode = async (urlOrDataUrl) => {
        // ✅ Important: naya reader per attempt (stopCamera->reset ke baad stable)
        const reader = new BrowserMultiFormatReader();
        const res = await reader.decodeFromImageUrl(urlOrDataUrl);
        return res?.getText?.() || "";
      };

      try {
        const img = await loadImage(originalUrl);

        // quick crops (QR aksar left me hota hai label pe)
        const leftCrop = {
          x: 0,
          y: 0,
          w: Math.round(img.width * 0.6),
          h: img.height,
        };
        const centerCrop = {
          x: Math.round(img.width * 0.2),
          y: 0,
          w: Math.round(img.width * 0.6),
          h: img.height,
        };
        const rightCrop = {
          x: Math.round(img.width * 0.4),
          y: 0,
          w: Math.round(img.width * 0.6),
          h: img.height,
        };

        const pad = Math.max(
          50,
          Math.round(Math.max(img.width, img.height) * 0.14)
        );

        // variants list (order matters)
        const variants = [
          originalUrl, // 1) raw
          canvasDataUrl({ img, pad, scale: 1 }), // 2) padded
          canvasDataUrl({ img, pad, scale: 2 }), // 3) padded + scaled
          canvasDataUrl({ img, crop: leftCrop, pad, scale: 2 }), // 4) left crop (QR likely)
          canvasDataUrl({ img, crop: centerCrop, pad, scale: 2 }), // 5) center crop
          canvasDataUrl({ img, crop: rightCrop, pad, scale: 2 }), // 6) right crop
          canvasDataUrl({ img, pad, scale: 2, threshold: 160 }), // 7) threshold
          canvasDataUrl({ img, crop: leftCrop, pad, scale: 2, threshold: 160 }), // 8) left + threshold
          canvasDataUrl({ img, crop: leftCrop, pad, scale: 2, threshold: 130 }), // 9) left + stricter
        ];

        let decoded = "";
        let lastErr = null;

        for (const v of variants) {
          try {
            decoded = await tryDecode(v);
            if (decoded) break;
          } catch (e) {
            lastErr = e;
          }
        }

        if (!decoded) {
          console.error(lastErr);
          toast.error(
            "QR detect nahi hua (image). Try different angle / less glare."
          );
          return;
        }

        doSearch(decoded);
        toast.success(`Image QR scanned ✅`);
      } catch (e) {
        console.error(e);
        toast.error("Image read fail. (Try another image)");
      } finally {
        URL.revokeObjectURL(originalUrl);
        setIsImageScanning(false);
      }
    },
    [doSearch, isCameraOn, stopCamera]
  );

  const startCamera = useCallback(async () => {
    if (!videoRef.current) return;
    if (!readerRef.current) readerRef.current = new BrowserMultiFormatReader();

    try {
      setIsCameraOn(true);
      lockRef.current = false;
      setLockScan(false);

      const controls = await readerRef.current.decodeFromVideoDevice(
        deviceId || undefined,
        videoRef.current,
        (result) => {
          if (!result) return;
          if (lockRef.current) return;

          const raw = result.getText();
          const code = extractItemCode(raw);
          if (!code) {
            toast.error("QR se item code nahi mila.");
            return;
          }

          lockRef.current = true;
          setLockScan(true);

          setSearchCode(code);
          setManualInput(code);
          setTab("result");
          toast.success(`Scanned: ${code}`);

          setTimeout(() => {
            lockRef.current = false;
            setLockScan(false);
          }, 1200);
        }
      );

      controlsRef.current = controls;
    } catch (e) {
      console.error(e);
      setIsCameraOn(false);
      toast.error("Camera start fail. HTTPS + permission allow karo.");
    }
  }, [deviceId]);

  const switchCamera = useCallback(() => {
    if (!devices.length) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    if (!next?.deviceId) return;

    stopCamera();
    setDeviceId(next.deviceId);
    setTimeout(() => startCamera(), 220);
  }, [devices, deviceId, stopCamera, startCamera]);

  useEffect(() => {
    readerRef.current = new BrowserMultiFormatReader();

    const load = async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);

        const back =
          list.find((d) => /back|rear|environment/i.test(d.label)) ||
          list[list.length - 1];

        if (back?.deviceId) setDeviceId(back.deviceId);
        else if (list?.[0]?.deviceId) setDeviceId(list[0].deviceId);
      } catch (e) {
        console.error(e);
        toast.error(
          "Camera devices load nahi ho paaye. HTTPS / permissions check karo."
        );
      }
    };

    load();

    const onVis = () => {
      if (document.hidden) stopCamera();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopCamera();
    };
  }, [stopCamera]);

  const clearAll = () => {
    setManualInput("");
    setSearchCode("");
    setTab("scan");
  };

  const statusTone =
    String(top?.status || "").toLowerCase() === "active" ? "emerald" : "slate";

  return (
    // <div className="">
      <div className="mx-auto max-w-7xl">
        <div className="h-[calc(100vh-140px)] md:h-[calc(100vh-70px)] bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 sm:px-5 py-3 bg-[#9481ce] text-white">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-[15px] sm:text-[17px] font-semibold truncate">
                    Labelled Stock • QR Search
                  </h2>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isFetching ? (
                  <Pill tone="amber">
                    <Spinner size={12} className="mr-1" /> Searching
                  </Pill>
                ) : (
                  <></>
                )}

                <Btn
                  variant="secondary"
                  onClick={() => navigate("/stocks/labelled-stock-list")}
                  className="bg-white text-[#6b4fd8] border-white/60 hover:bg-violet-50"
                >
                  Back
                </Btn>
              </div>
            </div>
          </div>

          {/* Mobile Tabs */}
          <div className="md:hidden px-3 py-2 bg-slate-50 border-b border-slate-200">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTab("scan")}
                className={`py-2 rounded-xl text-[12px] font-semibold border transition ${
                  tab === "scan"
                    ? "bg-[#9481ce] text-white border-[#9481ce]"
                    : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                Scan
              </button>
              <button
                onClick={() => setTab("result")}
                className={`py-2 rounded-xl text-[12px] font-semibold border transition ${
                  tab === "result"
                    ? "bg-[#9481ce] text-white border-[#9481ce]"
                    : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                Detail
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12">
              {/* LEFT */}
            <div
  className={`h-full md:border-r border-slate-200 overflow-y-auto md:overflow-hidden ${
    tab !== "scan" ? "hidden md:block" : ""
  } md:col-span-1 lg:col-span-8`}
>
                <div className="h-full p-3 sm:p-4 flex flex-col gap-3">
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col flex-1">
                    <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900">
                          Camera Scanner
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          QR — auto detect.
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* tiny image scan button */}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isImageScanning}
                          title="Scan from image"
                          className="h-9 w-9 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60"
                        >
                          {isImageScanning ? (
                            <Spinner size={14} />
                          ) : (
                            <FiImage />
                          )}
                        </button>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            scanFromImageFile(f);
                          }}
                        />

                        {devices.length > 1 && (
                          <Btn variant="secondary" onClick={switchCamera}>
                            <FiRefreshCcw /> Switch
                          </Btn>
                        )}

                        {!isCameraOn ? (
                          <Btn
                            variant="primary"
                            onClick={startCamera}
                            disabled={!deviceId && devices.length === 0}
                          >
                            <FiCamera /> Start
                          </Btn>
                        ) : (
                          <Btn variant="danger" onClick={stopCamera}>
                            <FiStopCircle /> Stop
                          </Btn>
                        )}
                      </div>
                    </div>



<div className="p-4 flex-1 min-h-0 flex flex-col">
  {/* Camera Box */}
  <div className="relative bg-black rounded-2xl overflow-hidden flex-1 min-h-[280px] pb-[92px] sm:pb-0">
    <video
      ref={videoRef}
      className="w-full h-full object-cover"
      muted
      playsInline
    />
    <ScanOverlay isOn={isCameraOn} isLock={lockScan} />

    {/* ✅ Search Bar (Mobile: overlay bottom, Desktop: normal below) */}
    <div className="absolute inset-x-0 bottom-0 p-3 sm:static sm:p-0 sm:mt-4">
      <div className="rounded-2xl bg-white/95 backdrop-blur border border-slate-200 p-2 shadow-sm">
        <div className="flex  sm:flex-row gap-2">
          <input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder="Paste QR text / Item Code (e.g. DGO1064)"
            className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-violet-400"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                doSearch(manualInput);
              }
            }}
          />

          <motion.button
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => doSearch(manualInput)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#9481ce]  text-white text-[12px] font-semibold px-4 py-2 hover:bg-[#9481ce] focus:outline-none focus:ring-2 focus:ring-[#9481ce] focus:ring-offset-2"
          >
            {isFetching ? (
              <>
                <Spinner size={14} /> 
              </>
            ) : (
              <>
                <FiSearch /> 
              </>
            )}
          </motion.button>
        </div>
      </div>
    </div>
  </div>
</div>



    
                  </div>

                  <div className="text-[11px] text-slate-500 px-1">
                    Tip: QR ko steady rakho, light achha rakho.
                  </div>
                </div>
              </div>

              {/* RIGHT (Detail) */}
              <div
                className={`h-full pt-2 pb-1 overflow-hidden ${
                  tab !== "result" ? "hidden md:block" : ""
                } md:col-span-1 lg:col-span-4`}
              >
                <div className="h-full flex flex-col">
                  <div className="px-4 sm:px-5 py-3 border-b bg-slate-50 flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-semibold text-slate-900">
                        Detail
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {searchCode ? `Search: ${searchCode}` : "No search yet"}
                      </div>
                    </div>
                    <Pill tone="violet">{searchCode ? rows.length : 0}</Pill>
                  </div>

                  <div className="flex-1 p-4 sm:p-5 overflow-y-auto">
                    {!searchCode ? (
                      <div className="max-w-sm text-center mx-auto pt-10">
                        <div className="mx-auto h-12 w-12 rounded-2xl bg-violet-100 text-violet-700 flex items-center justify-center">
                          <FiSearch size={20} />
                        </div>
                        <div className="mt-3 font-semibold text-slate-900">
                          Scan / Search to view details
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          Desktop pe yahi right side details show hoga.
                        </div>
                      </div>
                    ) : isFetching ? (
                      <div className="bg-white border border-slate-200 rounded-2xl p-4">
                        <div className="h-4 w-40 bg-slate-100 rounded mb-2 animate-pulse" />
                        <div className="h-3 w-56 bg-slate-100 rounded mb-4 animate-pulse" />
                        <div className="grid grid-cols-2 gap-2">
                          <div className="h-14 bg-slate-100 rounded-xl animate-pulse" />
                          <div className="h-14 bg-slate-100 rounded-xl animate-pulse" />
                          <div className="h-14 bg-slate-100 rounded-xl col-span-2 animate-pulse" />
                        </div>
                        <div className="mt-4 h-10 bg-slate-100 rounded-xl animate-pulse" />
                      </div>
                    ) : isSuccess && rows.length ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4"
                      >
                        {/* Title */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-[16px] font-semibold text-slate-900 truncate">
                              {top?.itemCode || "-"}
                            </div>
                            <div className="text-[12px] text-slate-500 truncate">
                              {top?.productName || "-"} •{" "}
                              {top?.categoryName || "-"} •{" "}
                              {top?.purityName || "-"}
                            </div>
                          </div>
                          <Pill tone={statusTone}>{top?.status || "-"}</Pill>
                        </div>

                        {/* Images */}
                        <div className="mt-4">
                          <div className="text-[12px] font-semibold text-slate-900">
                            Images
                          </div>

                          {imageUrls.length ? (
                            <div className="mt-2 grid grid-cols-3 gap-2">
                              {imageUrls.slice(0, 6).map((url, idx) => (
                                <motion.a
                                  key={url + idx}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  whileHover={{ scale: 1.02 }}
                                  className="block overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                                  title="Open"
                                >
                                  <img
                                    src={url}
                                    alt={`item-${idx}`}
                                    className="h-20 w-full object-cover"
                                    loading="lazy"
                                  />
                                </motion.a>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-2 text-[11px] text-slate-500">
                              No images available
                            </div>
                          )}
                        </div>

                        {/* Weights */}
                        <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                            <div className="text-[10px] text-slate-400">
                              Net Wt
                            </div>
                            <div className="font-semibold text-slate-800">
                              {top?.netWt ?? "-"}
                            </div>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                            <div className="text-[10px] text-slate-400">
                              Total Wt
                            </div>
                            <div className="font-semibold text-slate-800">
                              {top?.totalWt ?? "-"}
                            </div>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                            <div className="text-[10px] text-slate-400">
                              Stone Wt
                            </div>
                            <div className="font-semibold text-slate-800">
                              {top?.stoneWt ?? "-"}
                            </div>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                            <div className="text-[10px] text-slate-400">
                              Diamond Wt
                            </div>
                            <div className="font-semibold text-slate-800">
                              {top?.diamondWt ?? "-"}
                            </div>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 col-span-2">
                            <div className="text-[10px] text-slate-400">
                              Branch
                            </div>
                            <div className="font-semibold text-slate-800 truncate">
                              {top?.branchName ?? "-"}
                            </div>
                          </div>
                        </div>

                        {/* Rate + Amount */}
                        <div className="mt-4 border-t border-slate-200 pt-4">
                          {!top?.mrp && (
                            <div className="flex items-center justify-between">
                              <div className="text-[12px] font-semibold text-slate-900">
                                Latest Rate
                              </div>

                              {isRateFetching ? (
                                <Pill tone="amber">
                                  <Spinner size={12} /> Loading rate
                                </Pill>
                              ) : matchedRate ? (
                                <Pill tone="emerald">
                                  ₹{toNum(matchedRate.ratePerGram)}/g
                                </Pill>
                              ) : (
                                <Pill tone="rose">Rate not found</Pill>
                              )}
                            </div>
                          )}
                          {!top?.mrp && matchedRate && (
                            <div className="mt-2 text-[11px] text-slate-500">
                              {matchedRate?.displayInfo ||
                                (matchedRate?.effectiveDate
                                  ? String(matchedRate.effectiveDate).slice(
                                      0,
                                      10
                                    )
                                  : "")}
                            </div>
                          )}
                          {top?.mrp ? (
                            <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                                <div className="text-[10px] text-slate-400">
                                  Metal Amount
                                </div>
                                <div className="font-semibold text-slate-800">
                                  {top?.mrp ? fmtINR(top?.mrp) : "-"}
                                </div>
                              </div>
                              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                                <div className="text-[10px] text-slate-400">
                                  Total Amount
                                </div>
                                <div className="font-semibold text-slate-800">
                                  {top?.mrp ? fmtINR(top?.mrp) : "-"}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                                <div className="text-[10px] text-slate-400">
                                  Metal Amount
                                </div>
                                <div className="font-semibold text-slate-800">
                                  {matchedRate ? fmtINR(calc.metalAmount) : "-"}
                                </div>
                              </div>
                              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                                <div className="text-[10px] text-slate-400">
                                  Total Amount
                                </div>
                                <div className="font-semibold text-slate-800">
                                  {matchedRate ? fmtINR(calc.totalAmount) : "-"}
                                </div>
                              </div>
                            </div>
                          )}

                          {!top?.mrp && matchedRate && (
                            <div className="mt-2 text-[11px] text-slate-500">
                              Includes: Making {fmtINR(calc.makingAmount)},
                              Stone {fmtINR(calc.stoneAmount)}, Diamond{" "}
                              {fmtINR(calc.diamondAmount)}, Hallmark{" "}
                              {fmtINR(calc.hallmarkCharge)}
                            </div>
                          )}
                        </div>

                        {/* Mobile Back */}
                        <div className="mt-4 md:hidden">
                          <Btn variant="primary" onClick={() => setTab("scan")}>
                            <FiChevronLeft /> Back to Scan
                          </Btn>
                        </div>

                        {rows.length > 1 && (
                          <div className="mt-3 text-[11px] text-slate-500">
                            More matches:{" "}
                            <span className="font-semibold text-slate-900">
                              {rows.length - 1}
                            </span>{" "}
                            (open list)
                          </div>
                        )}
                      </motion.div>
                    ) : (
                      <div className="max-w-sm text-center mx-auto pt-10">
                        <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center">
                          <FiX size={20} />
                        </div>
                        <div className="mt-3 font-semibold text-slate-900">
                          No item found
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          For{" "}
                          <span className="font-semibold">{searchCode}</span>
                        </div>
                        <div className="mt-4 grid gap-2">
                          <Btn
                            variant="secondary"
                            onClick={() => setTab("scan")}
                          >
                            <FiCamera /> Try again
                          </Btn>
                          <Btn variant="secondary" onClick={clearAll}>
                            <FiX /> Clear
                          </Btn>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* <div className="px-4 sm:px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
                    <Btn variant="secondary" onClick={clearAll}>
                      Clear
                    </Btn>
                  </div> */}
                </div>
              </div>
              {/* end right */}
            </div>
          </div>
        </div>
      </div>
    // </div>
  );
};

export default LabelledStockQrScan;
